import { filter } from 'rxjs/operators';
import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Apollo, gql } from 'apollo-angular';
import { CalendarRegistrationForm, CalendarRegistrationFormInput, Mutation, Query, WorksheetColumnType } from 'core-lib';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { MessageService } from 'primeng/api';
@Component({
  selector: 'app-appointment-registration-form',
  templateUrl: './appointment-registration-form.component.html',
  styleUrls: ['./appointment-registration-form.component.scss'],
  providers: [{ provide: APP_BASE_HREF, useFactory: (s: PlatformLocation) => s.getBaseHrefFromDOM(), deps: [PlatformLocation] }]
})
export class AppointmentRegistrationFormComponent implements OnInit {

  registrationFormGroup: FormGroup;
  registrationForm: CalendarRegistrationForm;
  usedInEvents = false;

  formId: string;
  columnTypes: { label: string, value: WorksheetColumnType }[] = [];

  FORM_FIELDS = `name id header footer fields{ columnName propertyName datatype mandatory} usedInEventsCount`

  MUTATION_CREATE_FORM = gql`
  mutation createCalendarRegistrationForm($input : CalendarRegistrationFormInput!){
    createCalendarRegistrationForm(input : $input){
      ${this.FORM_FIELDS}
    }
  }`;

  MUTATION_UPDATE_FORM = gql`
  mutation updateCalendarRegistrationForm($id: ID!,$input : CalendarRegistrationFormInput!){
    updateCalendarRegistrationForm(id: $id, input : $input){
      ${this.FORM_FIELDS}
    }
  }`;

  QUERY_LOAD_FORM = gql`
  query getCalendarRegistrationForm($id: ID!){
    getCalendarRegistrationForm(id : $id){
      ${this.FORM_FIELDS}
    }
  }`;

  constructor(private apollo: Apollo, private dialogRef: DynamicDialogRef, private dialogConfig: DynamicDialogConfig, @Inject(APP_BASE_HREF) public baseHref: string, private messageService: MessageService) {
    this.formId = dialogConfig.data?.formId;
  }


  ngOnInit(): void {
    this.columnTypes = [
      { label: $localize`:@@appointment-registration-form.field-types.Text:Text`, value: WorksheetColumnType.Text },
      { label: $localize`:@@appointment-registration-form.field-types.boolean:Boolean`, value: WorksheetColumnType.Boolean },
      { label: $localize`:@@appointment-registration-form.field-types.Date:Date`, value: WorksheetColumnType.Date },
      { label: $localize`:@@appointment-registration-form.field-types.Float:Float`, value: WorksheetColumnType.Float },
      { label: $localize`:@@appointment-registration-form.field-types.Number:Number`, value: WorksheetColumnType.Number },
      { label: $localize`:@@appointment-registration-form.field-types.String:String`, value: WorksheetColumnType.String },
      { label: $localize`:@@appointment-registration-form.field-types.Enum:Enum`, value: WorksheetColumnType.Enum }
    ];

    this.columnTypes.sort((a, b) => {
      return a.label.localeCompare(b.label);
    });

    if (this.formId != null) {
      this.apollo.query<Query>({
        query: this.QUERY_LOAD_FORM,
        variables: {
          id: this.formId
        }
      }).subscribe((result) => {
        this.registrationForm = result.data.getCalendarRegistrationForm;
        this.usedInEvents = this.registrationForm.usedInEventsCount > 0
        this.registrationFormGroup = new FormGroup({
          name: new FormControl(this.registrationForm.name),
          header: new FormControl(this.registrationForm.header),
          footer: new FormControl(this.registrationForm.footer),
          fields: new FormArray([])
        });

        for (const field of this.registrationForm.fields) {
          const formGroup = new FormGroup({
            columnName: new FormControl(field.columnName),
            type: new FormControl(field.datatype),
            propertyName: new FormControl(field.propertyName),
            mandatory: new FormControl(field.mandatory)
          });
          this.fields.push(formGroup);
        }
      });
    } else {
      this.registrationFormGroup = new FormGroup({
        name: new FormControl(),
        header: new FormControl(),
        footer: new FormControl(),
        fields: new FormArray([])
      });
    }
  }

  onSave(): void {
    const input = this.registrationFormGroup.getRawValue() as CalendarRegistrationFormInput;
    if (this.formId) {
      this.apollo.query<Mutation>({
        query: this.MUTATION_UPDATE_FORM,
        variables: { id: this.formId, input: input }
      }).subscribe((result) => {
        if (!result.error) {
          this.dialogRef.close(result.data.updateCalendarRegistrationForm);
        }
      });
    } else {
      this.apollo.query<Mutation>({
        query: this.MUTATION_CREATE_FORM,
        variables: { input: input }
      }).subscribe((result) => {
        if (!result.error) {
          this.dialogRef.close(result.data.createCalendarRegistrationForm);
        }
      });
    }
  }

  /**
   * swap fields
   * @param pos1 
   * @param pos2 
   */
  onSwapFields(pos1, pos2): void {
    const temp = this.fields.controls[pos1];
    this.fields.controls[pos1] = this.fields.controls[pos2];
    this.fields.controls[pos2] = temp;
  }

  /**
   * remove existing field
   * @param index 
   */
  onRemoveField(index): void {
    this.fields.controls.splice(index, 1);
  }


  /**
   * add new field
   */
  onCreateField(): void {
    const formGroup = new FormGroup({
      columnName: new FormControl(),
      type: new FormControl(WorksheetColumnType.String),
      propertyName: new FormControl(),
      mandatory: new FormControl(true)
    });
    this.fields.push(formGroup);
  }

  /**
   * Copy to clipboard
   * @param propertyName 
   */
  copyToClipboard(propertyName: string): void {
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(propertyName).then(() => {
        this.messageService.add({ severity: 'success', summary: null, detail: $localize`:@@appointment-registration-form-id-copied:ID copied to clipboard` });
      }).catch(e => {
        const event = new CustomEvent('copy-to-clipboard', { detail: { text: propertyName } });
        window.dispatchEvent(event);
      });
    }
  }

  get fields(): FormArray {
    const fields = this.registrationFormGroup.get('fields') as FormArray;
    fields.controls[0]
    return fields;
  }

  hasEnumField(): boolean {
    return this.fields.controls.filter((group: FormGroup) => group.get('type').value === WorksheetColumnType.Enum).length > 0;
  }
}
